import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { CatalogService } from '../catalog-api/catalog.service';
import { ToasterService } from 'angular2-toaster';

const ES_RESULTS_SIZE = 10;

@Component({
    // tslint:disable-next-line:component-selector
	selector: 'bulk-title-search',
	templateUrl: 'bulk-title-search.component.html',
	styleUrls: ['bulk-title-search.component.scss'],
})
export class BulkTitleSearchComponent implements OnInit {
    // Two way value from the textarea element
    private textareaValue = '';

    // Results array that contains objects from each result
    private searchValues = [];
    private searchResults = [];

    // Have an array with all the results in order so we can reload results using page number
    private totalSearchValues = [];
    private totalSearchResults = [];

    // Page details
    private page = 0;
    private PAGE_SIZE = 50;
    private pagesAvailable = 0;

    // Booleans regarding state
    private searchLoading = false;

    // Boolean for extended search
    private extended = false;

    // Boolean for CSV loading
    private exportCsvLoading = false;

    // Event emitter to display the catalog manage component
    @Output() onHandleOpen = new EventEmitter();

    ngOnInit() {}

    constructor(private catalogService: CatalogService,
        private toasterService: ToasterService) {}

    get currentTextareaLines() {
        return this.textareaValue.split('\n').filter(item => item).length;
    }

    getPagesAvailable() {
        if (this.currentTextareaLines === 0) {
            return 0;
        } else {
            return Math.floor((this.currentTextareaLines - 1) / this.PAGE_SIZE);
        }
    }

    handleSearch() {
        this.searchLoading = true;
        this.searchResults = [];

        // Grab all valid entries from the textarea - filter out empty lines
        const allValidEntries = this.textareaValue.split('\n')
           .filter(item => item);


        // Grab only the entries required for the current page
        const indexStart = this.page * this.PAGE_SIZE;
        const indexEnd = indexStart + this.PAGE_SIZE;
        this.searchValues = allValidEntries.slice(indexStart, indexEnd);

        // Set the available pages
        this.pagesAvailable = this.getPagesAvailable();

        // Check if the page has already been searched and the results are in totalSearchResults
        if (this.totalSearchResults.length > indexStart && JSON.stringify(this.totalSearchValues) === JSON.stringify(allValidEntries)) {
            this.searchResults = this.totalSearchResults.slice(indexStart, indexEnd);
            this.searchLoading = false;
            return;
        }

        // The search values have changed, update them
        this.totalSearchValues = allValidEntries;

        // Call es and then filter
        Promise.all(this.searchValues.map((searchValue, index) => {
            const queryJSON = this.catalogService.createEsQuery(searchValue, ES_RESULTS_SIZE, [], null,
                this.catalogService.getEsDefaultSource());
            if (this.extended) {
                return new Promise((resolve, reject) => {
                    const imdbQueryJSON = this.catalogService.createEsQuery(
                        searchValue, ES_RESULTS_SIZE, [], null, [
                            'AkAs',
                            'Countries',
                            'ResourceId',
                            'PlotSummary',
                            'ProductionCompanies',
                            'Title',
                            'Year',
                        ],
                        'imdb'
                    );

                    Promise.all([
                        this.catalogService.callEsProxyCustomQuery(
                            queryJSON,
                            'master_catalog'
                        ),
                        this.catalogService.callEsProxyCustomQuery(
                            imdbQueryJSON,
                            'imdb_metadata',
                        )
                    ])
                    .then((res) => {
                        const imdbResArray = res[1].hits.hits.map(this.catalogService.mapEsImdbResultItem)
                            .filter(item => !item.parrotId);
                        const results = res[0].hits.hits.map(this.catalogService.mapEsResultItem)
                            .concat(
                                imdbResArray
                            );
                        this.searchResults[index] = results.map((item) => {
                            item.displayNameShort = item.displayName.length > 23 ?
                                item.displayName.slice(0, 23).concat('...') :
                                item.displayName;
                            return item;
                        });

                        // Select the first item in the index if it exists.
                        if (this.searchResults[index].length > 0) {
                            this.searchResults[index][0].checked = true;
                        }
                        // Add the results to the total search results
                        this.totalSearchResults[index + indexStart] = this.searchResults[index];
                        resolve();
                    })
                    .catch((err) => {
                        console.error('Failed to call the ES proxy in the catalog API' + err.toString());
                        this.toasterService.pop('error', 'Failed to call the ES proxy in the catalog API');
                        reject();
                    });
                });
            } else {
                return new Promise((resolve, reject) => {
                    this.catalogService.callEsProxyCustomQuery(queryJSON)
                        .then((res) => {
                            this.searchResults[index] = res.hits.hits.map(this.catalogService.mapEsResultItem);
                            this.searchResults[index] = this.searchResults[index].map((item) => {
                                item.displayNameShort = item.displayName.length > 23 ?
                                    item.displayName.slice(0, 23).concat('...') :
                                    item.displayName;
                                return item;
                            });
                            // Select the first item in the index if it exists.
                            if (this.searchResults[index].length > 0) {
                                this.searchResults[index][0].checked = true;
                            }

                            // Add the results to the total search results
                            this.totalSearchResults[index + indexStart] = this.searchResults[index];
                            resolve();
                        })
                        .catch((err) => {
                            console.error('Failed to load for search value ' + searchValue);
                            this.searchResults[index] = [];
                            this.toasterService.pop('error', 'Failed to call the ES proxy in the catalog API');
                            reject();
                        });
                });
            }
        }))
        .then(() => {
            this.searchLoading = false;
        })
        .catch(() => {});
    }

    mapSearchResults(results) {
        const resultsCombined = [];
        results.forEach((item, index) => {
            // If there doesnt exist a checked entry, create an empty entry for title requests.
            let checkedPresent = false;
            item.forEach((itemNested) => {
                if (itemNested.checked) {
                    checkedPresent = true;
                }
                resultsCombined.push(itemNested);
            });
            if (!checkedPresent) {
                resultsCombined.push({
                    displayName: this.searchValues[index],
                    empty: true,
                });
            }
        });
        return resultsCombined;
    }

    clearTotalSearchValues() {
        this.totalSearchValues = [];
    }

    handleSelected(result, index) {
        // Can only be one result selected per search value
        const selectedResult = this.searchResults[index]
            .find(item => item.checked);
        if (selectedResult) {
            selectedResult.checked = false;
        }
        if (selectedResult !== result) {
            result.checked = true;
        }
    }

    handleOpen(result) {
        this.onHandleOpen.emit(result);
    }

    handlePreviousPage() {
        if (this.page > 0) {
            this.page = this.page - 1;
            this.handleSearch();
        }
    }

    handleNextPage() {
        if (this.page < Math.floor((this.currentTextareaLines / this.PAGE_SIZE))) {
            this.page = this.page + 1;
            this.handleSearch();
        }
    }

    handleExportToCSV() {
        this.exportCsvLoading = true;
        const csvData = this.convertToCSV();
        const blob = new Blob([csvData], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const currentTime = new Date();

        if (navigator.msSaveOrOpenBlob) {
            navigator.msSaveBlob(blob, `bulk-title-search-${currentTime.getTime()}.csv`);
        } else {
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = `bulk-title-search-${currentTime.getTime()}.csv`;
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
        }
        window.URL.revokeObjectURL(url);
        this.exportCsvLoading = false;
    }

    convertToCSV() {
        // Grab all valid entries from the textarea - filter out empty lines
        const allValidEntries = this.textareaValue.split('\n')
           .filter(item => item);
        let csvData = `"search_term","parrot_id","title","catalog_state"\r\n`;
        csvData = csvData.concat(
            this.totalSearchValues.map((item, index) => {
                const selectedTitle = this.totalSearchResults[index] && this.totalSearchResults[index].find(result => result.checked);
                if (selectedTitle) {
                    return `"${item}","${selectedTitle.parrotId}","${selectedTitle.title}","${selectedTitle.catalogState}"`;
                } else {
                    return `"${item}",,,`;
                }
            }).join('\r\n')
        );
        return csvData;
    }

    clearTextarea() {
        this.textareaValue = '';
    }
}



// WEBPACK FOOTER //
// ./src/app/catalogue/bulk-title-search/bulk-title-search.component.ts